import React, { useEffect, useRef, useState } from "react";
import { ImageListItem, ImageListItemBar } from "@mui/material";
import ImageIcon from "@material-ui/icons/Image";
import BlurOnIcon from "@material-ui/icons/BlurOn";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useIntl } from "react-intl";
import { useStyles } from "./BackgroundImageItem.style";
import {
  userPreferencesService,
  videoFilterService,
} from "../../../../../../../../Providers/ServiceProvider/ServiceProvider";
import { useCustomerId } from "../../../../../../../../Providers/CustomerProvider/CustomerProvider";
import { FileMetadata } from "../../../../../../../../generated/video-api";
import ButtonLoadingWrapper from "../../../../../../../../Components/Buttons/ButtonLoadingWrapper/ButtonLoadingWrapper";
import { UserVideoBackgroundType } from "../../../../../../../../generated/user-preferences-api";
import { messages } from "./BackgroundImageItem.messages";

interface Props {
  type: UserVideoBackgroundType;
  file?: FileMetadata;
  selectedFilterType: UserVideoBackgroundType;
  selectedFilterFileId?: string;
  onSelected: () => void;
  userId: string;
}

const BackgroundImageItem = (props: Props) => {
  const intl = useIntl();
  const classes = useStyles();
  const customerId = useCustomerId();
  const {
    type,
    file,
    selectedFilterType,
    selectedFilterFileId,
    onSelected,
    userId,
  } = props;
  const [isLoaded, setIsLoaded] = useState<boolean>();
  const imgRef = useRef<HTMLImageElement>(null);

  const getItem = () => {
    if (type === UserVideoBackgroundType.File) {
      return <ImageIcon className={classes.imgPlaceholder} />;
    }
    if (type === UserVideoBackgroundType.Blur) {
      return <BlurOnIcon className={classes.imgPlaceholder} />;
    }
    return (
      <DoNotDisturbAltIcon
        className={classes.imgPlaceholder}
        style={{ fontSize: 150 }}
      />
    );
  };

  const getTitle = () => {
    if (type === UserVideoBackgroundType.File && file) {
      return file.fileName;
    }
    if (type === UserVideoBackgroundType.Blur) {
      return intl.formatMessage(messages.blurTitle);
    }
    return intl.formatMessage(messages.noneTitle);
  };

  const isItemSelected = () => {
    switch (selectedFilterType) {
      case UserVideoBackgroundType.None:
        return type === UserVideoBackgroundType.None;
      case UserVideoBackgroundType.Blur:
        return type === UserVideoBackgroundType.Blur;
      case UserVideoBackgroundType.File:
        return (
          type === UserVideoBackgroundType.File &&
          file?.fileId === selectedFilterFileId
        );
      default:
        return false;
    }
  };

  const loadImage = () => {
    if (customerId && file) {
      setIsLoaded(false);
      videoFilterService()
        .downloadFilterRaw({
          customerId,
          fileId: file.fileId,
        })
        .then((res) => Promise.resolve(res.raw.blob()))
        .then((blob) => {
          setIsLoaded(true);
          if (imgRef !== null) {
            // @ts-ignore
            imgRef.current.src = URL.createObjectURL(blob);
          }
        });
    }
  };

  const handleClick = () => {
    userPreferencesService()
      .updateVideoBackground({
        userId,
        updateUserVideoBackgroundRequest: {
          videoBackgroundType: type,
          videoBackgroundFileId: file?.fileId,
        },
      })
      .then(() => onSelected());
  };

  useEffect(() => {
    loadImage();
  }, [file?.fileId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ImageListItem
      onClick={() => handleClick()}
      component="div"
      className={isItemSelected() ? classes.selectedItem : classes.item}
      style={{ width: 150, height: 150 }}
    >
      {isLoaded ? (
        <img
          className={classes.img}
          ref={imgRef}
          alt={file?.fileName}
          loading="lazy"
        />
      ) : (
        <ButtonLoadingWrapper isLoading={type === UserVideoBackgroundType.File}>
          {getItem()}
        </ButtonLoadingWrapper>
      )}
      <ImageListItemBar
        sx={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, " +
            "rgba(0,0,0,0.4) 80%, rgba(0,0,0,0) 100%)",
        }}
        className={classes.imgBar}
        subtitle={getTitle()}
        position="top"
      />
      {isItemSelected() && (
        <CheckCircleIcon style={{ position: "absolute", fontSize: 40 }} />
      )}
    </ImageListItem>
  );
};

export default BackgroundImageItem;
